import { useEffect, useSyncExternalStore } from 'react';

/**
 * The scene behind the screen (docs/LONG_NIGHT_ART_DIRECTION.md §5–§6): the smoke's palette,
 * speed and twist, the lamp hanging over the table, and the stars. Screens set it; the smoke
 * (SpaceBackground), the lamp (Lamp) and the stars read it. The smoke never cuts: a change
 * cross-fades over 2.4 s. Outside the Long Night the scene is the void as it always was.
 */

/** "#rrggbb" as 0–1 channels. */
export type Rgb = readonly [number, number, number];

export const rgb = (hex: string): Rgb => {
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

export interface Palette {
  readonly void: Rgb;
  readonly deep: Rgb;
  readonly body: Rgb;
  readonly current: Rgb;
  readonly wisp: Rgb;
  /** Relative to the void as it always was (1). */
  readonly speed: number;
  readonly twist: number;
}

const palette = (
  colors: readonly [string, string, string, string, string],
  speed: number,
  twist: number,
): Palette => ({
  void: rgb(colors[0]),
  deep: rgb(colors[1]),
  body: rgb(colors[2]),
  current: rgb(colors[3]),
  wisp: rgb(colors[4]),
  speed,
  twist,
});

/** The smoke's palettes (§6.4). */
export const SMOKE = {
  void: palette(['#050410', '#1b1242', '#5c2999', '#17338c', '#b8adff'], 1, 1),
  night2: palette(['#04040d', '#131540', '#3c2c8c', '#0f2a7a', '#a3b2ff'], 0.85, 1.1),
  night3: palette(['#030509', '#0c1a2b', '#1f3f5a', '#0e3a44', '#92d4c2'], 0.7, 1.25),
  tallow: palette(['#070504', '#1f150a', '#6b4a1c', '#2a1c3a', '#ffe3a8'], 0.6, 0.9),
  magpie: palette(['#03050a', '#0a1626', '#183a5a', '#1f4a3a', '#c9d6ff'], 1.3, 1),
  widow: palette(['#050506', '#121416', '#2e3436', '#173a30', '#cfe9df'], 0.45, 1.2),
  backRoom: palette(['#060203', '#160609', '#3d0f1c', '#1a0a14', '#ff9aa8'], 0.3, 1.4),
  closing: palette(['#020204', '#0d0a0a', '#2a1414', '#141018', '#e9cf94'], 0.25, 1.6),
  dawn: palette(['#0a0812', '#2b1d3a', '#7a4a6a', '#3a4a7a', '#ffd9a0'], 0.6, 0.6),
  afterHours: palette(['#040404', '#121212', '#2a2a2e', '#18181c', '#d8d8e0'], 1, 1),
  // The bosses after Night III (night/Horrors.tsx): each its own weather.
  storm: palette(['#02040a', '#0a1426', '#22385c', '#14223e', '#cfe4ff'], 1.6, 1.3),
  turnedTable: palette(['#050402', '#16120a', '#4a3a1a', '#1e2a16', '#ffe2a0'], 0.5, 2.2),
  switch: palette(['#04020a', '#140a26', '#3e1c66', '#1c1640', '#e0c8ff'], 0.9, 1.4),
  lightsOut: palette(['#010101', '#070506', '#1c1210', '#0c0a0c', '#ffcf8a'], 0.35, 1),
  cleanSlate: palette(['#050608', '#141820', '#363c48', '#202634', '#f0ece0'], 0.7, 0.8),
} as const;
export type SmokeName = keyof typeof SMOKE;

/** While a Lot is opened, its kind tints the smoke's body (§6.4). */
export const LOT_TINTS: Readonly<Record<string, string>> = {
  card: '#1f4fb0',
  hearsay: '#5c2999',
  appraisal: '#6e5223',
  curio: '#2f5f55',
  carver: '#5a3a22',
  mourning: '#1e5a48',
  omen: '#164a52',
  trick: '#7a2a4a',
  estate: '#3d0f1c',
};

export interface Scene {
  readonly smoke: SmokeName;
  /** Multiplies the palette's own speed and twist (the Counter turns at half speed). */
  readonly speed: number;
  readonly twist: number;
  /** 0–1: the table's own colors drained (a lost run, 0.7). */
  readonly desaturate: number;
  /** The smoke turning backward (a lost run). */
  readonly reverse: boolean;
  /** The body and the current trading places over 40 s (the Magpie's table). */
  readonly iridescent: boolean;
  /** A Lot being opened: its kind's color in the body. */
  readonly tint: string | null;
  /** A hush before a reveal: the smoke at a fifth of its speed (§9.1). */
  readonly hush: boolean;
  /** Closing Time, on the player's turn: the smoke holds still (§11.17). */
  readonly held: boolean;
  /** The lamp: its brightness (1 at Night I, 0 when out), whether its pool is narrow. */
  readonly lamp: number;
  /** When it goes out, it gutters twice first, after a second of silence (a lost run, §11.20). */
  readonly gutter: boolean;
  readonly narrow: boolean;
  /** In debt the lamp's breath deepens: it knows (§5.1). */
  readonly debt: boolean;
  /** The lamp's light tinted (a High Table takes its regular's color). */
  readonly lampTint: string | null;
  /** Where the pool falls, as a fraction of the screen's height from the top. */
  readonly focus: number;
  /** 0–1: how many stars show; whether any twinkle. */
  readonly stars: number;
  readonly twinkle: boolean;
}

export const DEFAULT_SCENE: Scene = {
  smoke: 'void',
  speed: 1,
  twist: 1,
  desaturate: 0,
  reverse: false,
  iridescent: false,
  tint: null,
  hush: false,
  held: false,
  lamp: 0,
  gutter: false,
  narrow: false,
  debt: false,
  lampTint: null,
  focus: 0.45,
  stars: 1,
  twinkle: true,
};

/** A regular's smoke and lamp tint (src/ai/personality.ts REGULARS: Tallow, Magpie, Widow). */
export const REGULAR_SMOKE: readonly SmokeName[] = ['tallow', 'magpie', 'widow'];
export const REGULAR_LAMP: readonly string[] = ['#ffd49a', '#c9d6ff', '#cfe9df'];

/**
 * The night deepens (§6.5): the smoke, the lamp and the stars of Night 1–3, Closing Time (4) and
 * After Hours (5 and on).
 */
export function nightScene(night: number): Partial<Scene> {
  if (night <= 1) return { smoke: 'void', lamp: 1, stars: 1, twinkle: true, narrow: false };
  if (night === 2) return { smoke: 'night2', lamp: 0.85, stars: 0.8, twinkle: true, narrow: false };
  if (night === 3) return { smoke: 'night3', lamp: 0.7, stars: 0.55, twinkle: true, narrow: false };
  if (night === 4)
    return { smoke: 'closing', lamp: 0.55, stars: 0.2, twinkle: false, narrow: true };
  return { smoke: 'afterHours', lamp: 0, stars: 0, twinkle: false, narrow: false };
}

let current: Scene = DEFAULT_SCENE;
/** A flare (§9.2) burns until this time (performance.now()). */
let flareUntil = 0;
const listeners = new Set<() => void>();

const emit = () => {
  for (const l of listeners) l();
};

/** Sets the scene: what isn't given goes back to its default. */
export function setScene(next: Partial<Scene>): void {
  const scene = { ...DEFAULT_SCENE, ...next };
  if (sameScene(scene, current)) return;
  current = scene;
  emit();
}

/** Changes part of the scene, keeping the rest. */
export function patchScene(next: Partial<Scene>): void {
  const scene = { ...current, ...next };
  if (sameScene(scene, current)) return;
  current = scene;
  emit();
}

export const resetScene = (): void => setScene({});

export const getScene = (): Scene => current;

export function subscribeScene(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const useScene = (): Scene => useSyncExternalStore(subscribeScene, getScene, getScene);

/** One chain wins the round on its own: the lamp flares and the smoke's wisp turns to lamplight. */
export function flare(ms = 1500): void {
  flareUntil = performance.now() + ms;
  emit();
}

export const flaring = (now: number): boolean => now < flareUntil;

function sameScene(a: Scene, b: Scene): boolean {
  return (Object.keys(a) as (keyof Scene)[]).every((k) => a[k] === b[k]);
}

/**
 * What the page itself shows of the scene: how many stars (a CSS variable on the root, which the
 * star field fades to), whether any twinkle, and the scene's name for styles that follow it.
 */
export function useSceneOnRoot(): void {
  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const s = getScene();
      root.style.setProperty('--star-level', String(s.stars));
      root.dataset.twinkle = s.twinkle ? 'on' : 'off';
      root.dataset.smoke = s.smoke;
      root.toggleAttribute('data-hush', s.hush);
      // Under the lamp the glow budget applies (§5.3), and the Long Night's primary action is brass.
      root.toggleAttribute('data-lamp', s.lamp > 0);
    };
    apply();
    return subscribeScene(apply);
  }, []);
}
